import React, { useRef, useContext, useEffect, useCallback } from 'react';

import mapStyle from '../../../styles/map-style.json';
import { Context } from '../Context/StoreLocatorContext';

import { NearbyStore } from '../../../types';
import { StyledMap } from './styles';

const labels = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

interface Props {
  origin: Record<string, any>;
  nearbyStores?: NearbyStore[];
  setStoreToDisplay: () => void;
  bladeState: boolean;
  setBladeState: () => void;
  storeToDisplay?: Record<string, any>;
  setShowSelectBtn: () => void;
}

export const GoogleMap = ({
  origin,
  nearbyStores = [],
  setStoreToDisplay,
  bladeState,
  setBladeState,
  storeToDisplay,
  setShowSelectBtn,
}: Props) => {
  const userLocationMarker = useRef();
  // * Ref to the element where we mount the map
  const ref = useRef(null);
  // * Ref to the map element
  const map = useRef(null);
  // * Ref to hold all
  const markers = useRef([]);

  // * Bool to check if the google script has loaded
  const { isGoogleLoaded } = useContext(Context);

  // * Func to create markers, and add a label to each marker
  const createMarker = useCallback((store, index) => {
    const {
      commonInfo: {
        address: { latitude: lat, longitude: lng },
      },
    } = store;
    if (!map.current) return null;
    const marker = new window.google.maps.Marker({
      position: { lat, lng },
      optimized: false,
      label: { text: labels[index], color: '#ffffff' },
      map: map.current,
    });
    // * Set the store of the marker that was clicked to be displayed in the blade. Set the blade to open
    marker.addListener('click', () => {
      setStoreToDisplay(store);
      setShowSelectBtn(true);
      setBladeState(!bladeState);
    });
    return marker;
  }, [bladeState, setBladeState, setShowSelectBtn, setStoreToDisplay]);

  const clearMarkers = () => {
    markers.current.forEach((marker) => {
      marker.setMap(null);
    });
    markers.current = [];
  };

  // * Takes a zoom value and zooms the map to this value. The default zoom if none provided is 9
  const changeZoom = (number = 9) => {
    map.current.setZoom(number);
  };

  // * Whenever the user's location changes, we want to drop a marker to identify the starting lcoation, and focus the map to that area
  useEffect(() => {
    if (map.current && origin) {
      // eslint-disable-next-line no-unused-vars
      userLocationMarker.current = new window.google.maps.Marker({
        position: origin,
        optimized: false,
        map: map.current,
      });
      map.current.setCenter(origin);
      if (nearbyStores && !nearbyStores.length) changeZoom(12);
    }
  }, [nearbyStores, origin]);

  // * Whenever we open a blade to show a store. Center the map to that store
  useEffect(() => {
    if (map.current && storeToDisplay) {
      const {
        commonInfo: {
          address: { latitude, longitude },
        },
      } = storeToDisplay;
      const location = new window.google.maps.LatLng(
        latitude,
        longitude + 0.03,
      );
      map.current.setCenter(location);
      changeZoom(12);
    }
  }, [storeToDisplay]);

  // * Everytime the user searchs in a different location, drop pins to identify each nearby location
  useEffect(() => {
    if (nearbyStores && map.current) {
      clearMarkers();
      const bounds = new window.google.maps.LatLngBounds();

      markers.current = nearbyStores.map((store, index) => {
        const marker = createMarker(store, index);
        bounds.extend(marker.getPosition());

        return marker;
      });
      map.current.fitBounds(bounds);
      if (map.current.getZoom() <= 7) {
        const center = markers.current[0].getPosition();
        map.current.setCenter(center);
        map.current.setZoom(7);
      }
    }
  }, [createMarker, nearbyStores]);

  // * Once our element has mounted, and google is loaded, we create a google map
  useEffect(() => {
    if (isGoogleLoaded && ref.current && window?.google?.maps?.Map) {
      const { Map: GMap } = window.google.maps;
      map.current = new GMap(ref.current, {
        center: origin,
        zoom: 10,
        styles: mapStyle,
      });

      if (nearbyStores) {
        markers.current = nearbyStores.map((store, index) => createMarker(store, index));
      }
    }
  }, [createMarker, isGoogleLoaded, nearbyStores, origin]);

  return <StyledMap ref={ref} />;
};
